import { __decorate, __metadata } from "tslib";
import { HttpClient } from '@angular/common/http';
import { AutoUnsubscribe, takeWhileAlive } from '../../utils';
// deep imports to make jest work
import { DestroyRef } from '@angular/core';
import { CrudQueryBuilder, CrudRequestEventType } from '@core/shared/crud';
import { ModelFactoryProvider } from '@core/shared/model';
import { CrudRequestType } from '@core/shared/request';
import { BehaviorSubject } from 'rxjs';
import { filter, map, share, skip, take } from 'rxjs/operators';
import { ClientCacheRequestMode, ClientCacheService } from '../../client-cache';
import { CrudRequestHelper, CrudService, CrudServiceFactory } from '../crud-service';
// not injected, use ItemServiceFactory/CrudServiceFactory to create!
let BaseItemService = /*#__PURE__*/(() => {
  let BaseItemService = class BaseItemService {
    get loadingState$() {
      return this.crud.loadingState$;
    }
    get pendingRequests$() {
      return this.crud.pendingRequests$;
    }
    get hasReadItems() {
      return this._hasReadItems;
    }
    set hasReadItems(state) {
      this._hasReadItems = state;
    }
    constructor(http, modelFactoryProvider, clientCache, crudServiceFactory, destroyRef) {
      this.http = http;
      this.modelFactoryProvider = modelFactoryProvider;
      this.clientCache = clientCache;
      this.crudServiceFactory = crudServiceFactory;
      this.destroyRef = destroyRef;
      // ItemServices now have a separate CrudQuery which is managed by themselves.
      // this is because CrudService query is used for other concepts.
      // ItemServices use it for list data querying and have a transient query that should not auto-reset.
      this.query = new CrudQueryBuilder();
      this.itemsAndRangeInfo$ = new BehaviorSubject(null);
      this.clientCacheConfig = false;
      this._hasReadItems = false;
    }
    setupCrudService(input) {
      if (input instanceof CrudService) {
        this.crud = input.withCustomQuery(this.query);
        this.ItemFactory = this.crud.ItemFactory;
      } else {
        this.ItemFactory = this.modelFactoryProvider.createModelFactory(input);
        this.crud = this.crudServiceFactory.useDestroyRef(this.destroyRef).createCrudService(input, this.query);
      }
      this.crud.listenToCrudStateEvents$().pipe(takeWhileAlive(this.destroyRef)).subscribe(event => {
        function getItems(e) {
          if (!e.response) return null;
          if (Array.isArray(e.response)) return e.response; // item array response
          if (typeof e.response === 'object') {
            if (Array.isArray(e.response.items)) {
              return e.response.items; // paged items response
            } else if (typeof e.response['id'] !== 'undefined') {
              return [e.response]; // single item response
            }
          }
          return null;
        }
        if (event.eventType === CrudRequestEventType.RequestCompleted) {
          if (CrudRequestHelper.requestTypeIsRead(event.crudAction)) {
            // read-requests have no side effects as they do not modify data.
            // A RequestCompleted event will be fired when finishing a full/partial refresh request, too.
            // However, newly fetched items will be processed by request subscriber actively.
            return;
          }
          if (event.crudAction === CrudRequestType.Update) {
            // a data modification request has been completed.
            // try to update changed items without reloading everything.
            const newItems = getItems(event);
            if (newItems && event.requestInfo.ids) {
              // event contains updated items as well as 
              this.doPartialListUpdate(newItems);
            } else {
              // could not handle correctly, initiate full reload
              this.refreshAllItems();
            }
          } else {
            // CRUD remove/create actions always require a full reload
            this.refreshAllItems();
          }
        }
      });
    }
    useCrudEndpoint(name) {
      if (!this.crud) throw new Error('called useCrudEndpoint before Model/CrudService is defined!');
      this.crud.useCrudEndpoint(name);
      return this; // allows to chain endpoint configuration right after injection/instancing of service
    }
    read(completeObservable = false, refresh = false) {
      this.assertInitialized();
      let tmp = this.getItems$();
      if (!this.hasReadItems) {
        this._readItems();
      } else {
        if (refresh) {
          // skip 1 to make sure refreshed data will be emitted and not old one (due to take(1))
          tmp = tmp.pipe(skip(1));
          this.refreshAllItems();
        }
      }
      // Filter out data which is no array (e.g. initial null value)
      tmp = tmp.pipe(filter(val => {
        return Array.isArray(val);
      }));
      if (completeObservable) tmp = tmp.pipe(take(1));
      return tmp;
    }
    refreshSomeItems(ids) {
      // already has this.query assigned! no need to use withCustomQuery!
      const req = this.crud.readSome(ids, {
        cache: ClientCacheRequestMode.wipeCache
      }).pipe(take(1), share());
      req.subscribe(items => {
        this.doPartialListUpdate(items);
      });
      return req;
    }
    doPartialListUpdate(incomingItems) {
      const existingItems = [...(this.getCurrentItems() || [])];
      const rangeInfo = this.getCurrentRangeInfo();
      const updatedItems = existingItems.map(item => {
        const replacement = incomingItems.find(item2 => item2.id === item.id);
        return replacement || item;
      });
      this.itemsAndRangeInfo$.next({
        items: updatedItems,
        rangeInfo
      });
    }
    // ---- Virtual sorting -------------------------------------------------------
    setVirtualSortBy(sort, order = 'ASC') {
      this.virtualSort = sort;
      this.virtualSortOrdering = order;
      return this;
    }
    updateItemListAndRange(items, rangeInfo, options = {}) {
      if (options.appendItems) {
        // in lazy mode items are appended instead of replacing previously
        items = [...(this.getCurrentItems() || []), ...items];
      }
      if (this.virtualSort) {
        items = [...items].sort(this.virtualSort);
      }
      if (this.virtualSortOrdering === 'DESC') {
        items.reverse();
      }
      // TODO: when can rangeInfo become null?
      /**
       * Aims to fix a problem with nestjsx/crud.
       * The rangeinfo returned from server has no clear info on how many items per page were requested.
       * As services have no imperative state where itemsPerPage could be looked up,
       * this helper method tries to get the info from query builder before sending the updated rangeInfo.
       */
      if (rangeInfo !== null) {
        const queryRange = this.query.getRange();
        rangeInfo.itemsPerPage = options?.knownItemsPerPage || queryRange.limit || null;
      }
      this.itemsAndRangeInfo$.next({
        items,
        rangeInfo
      });
    }
    // ------ CrudRangeInfo / RangeInfo ---------------------------------------------------
    getItemsAndRangeInfo$() {
      return this.itemsAndRangeInfo$.pipe(takeWhileAlive(this.destroyRef));
    }
    getRangeInfo$() {
      return this.itemsAndRangeInfo$.pipe(map(data => data?.rangeInfo || null), takeWhileAlive(this.destroyRef));
    }
    getItems$() {
      return this.itemsAndRangeInfo$.pipe(map(data => data?.items || null), takeWhileAlive(this.destroyRef));
    }
    getCurrentItemsAndRangeInfo() {
      return this.itemsAndRangeInfo$.getValue();
    }
    getCurrentRangeInfo() {
      return this.itemsAndRangeInfo$.getValue()?.rangeInfo || null;
    }
    getCurrentItems() {
      return this.itemsAndRangeInfo$.getValue()?.items || null;
    }
    // NOTE: a bit different than CrudService which implements separate CREATE/UPDATE methods.
    // left here for reference in case it is needed again...
    // save(item:Partial<TItemModel>):Observable<any> {
    // 	this.assertInitialized();
    // 	item = this.processItemData(item);
    // 	// console.log('post item',item)
    // 	const req = this.http.post(this.generateApiPath(item),item).pipe(
    // 		tap(()=>this.clearItemClientCache()),
    // 		share(),
    // 		map((response:IApiResponse<TItemModel>):TItemModel => {
    // 			return extractDataFromApiResponse(response, 'ItemService::save '+this.ItemFactory.Model.name);
    // 		})
    // 	);
    // 	req.subscribe(
    // 		(msg)=>{this.refreshAllItems()}
    // 	);
    // 	return req;
    // }
    assertInitialized() {
      if (!this.ItemFactory) throw new Error('Tried to use ItemService before a Model has been set!');
      if (!this.crud) throw new Error('Tried to use ItemService before CrudService has been set!');
      this.crud.assertReady();
    }
  };
  BaseItemService = __decorate([AutoUnsubscribe(), __metadata("design:paramtypes", [HttpClient, ModelFactoryProvider, ClientCacheService, CrudServiceFactory, DestroyRef])], BaseItemService);
  return BaseItemService;
})();
export { BaseItemService };